import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Chip,
    Grid,
    Link,
    useTheme,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";

import { ActualLine, ActualHeaderLine } from "./ActualLine";
import {
    openLinePopup,
    selectSearchedLineIdsSet,
    selectProjectCurrency,
} from "./actualSlice";
import { formattedCurrency } from "@/utils";

export default function ActualsGroup({
    group,
    registerSetExpand,
    selectable,
    getProject,
}) {
    const theme = useTheme();
    const dispatch = useDispatch();

    const [expanded, setExpanded] = useState(false);

    const isRoot = group.line_items_grouped != null || group.isCustomLineGroup;
    const children = group.line_items_grouped || group.children || [];
    const hasLines = group.line_items && group.line_items.length > 0;
    const isLeaf = hasLines && !group.isCustomLineGroup;

    const projectCurrency = useSelector(selectProjectCurrency);
    const searchedLineIdsSet = useSelector(selectSearchedLineIdsSet);
    const midLineIndex = hasLines
        ? Math.floor(group.line_items.length / 2)
        : null;

    let groupStack = group.line_items_grouped
        ? [...group.line_items_grouped]
        : [group];
    let hasAnyNestedLine = false;
    while (groupStack.length > 0) {
        const currGroup = groupStack.pop();
        const lineBeingSearched = (line) =>
            line.actual_line != null &&
            (!searchedLineIdsSet
                ? true
                : line.actual_line.id in searchedLineIdsSet);
        hasAnyNestedLine = currGroup.line_items.some(lineBeingSearched);
        if (hasAnyNestedLine) {
            break;
        }
        groupStack = groupStack.concat(currGroup.children);
    }
    if (!hasAnyNestedLine) {
        return;
    }

    registerSetExpand(setExpanded);

    return (
        <Accordion
            onChange={() => {
                setExpanded(!expanded);
            }}
            expanded={expanded}
            sx={{
                fontSize: 14,
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreRoundedIcon />}
                sx={{
                    border: "none",
                    background:
                        (isRoot || isLeaf) && theme.palette.background.default,
                    borderRadius: 1,
                }}
            >
                <Grid container alignItems={"center"} flexWrap="nowrap">
                    <Grid item>
                        {isRoot && group.id ? (
                            <Link href={`/bids/${group.id}`}>{group.name}</Link>
                        ) : (
                            <span
                                style={{
                                    fontSize: isLeaf && 12,
                                    marginLeft: isLeaf && 10,
                                }}
                            >
                                {group.name}
                            </span>
                        )}
                    </Grid>
                    {isRoot && group.bid_type && (
                        <Grid item sx={{ marginLeft: "36px" }}>
                            <Chip label={group.bid_type} />
                        </Grid>
                    )}
                    <Grid item flexGrow={2}>
                        {/* Spacer */}
                    </Grid>
                    <Grid
                        item
                        container
                        direction="column"
                        flexWrap="noWrap"
                        alignItems="flex-end"
                        sx={{ width: "auto", marginRight: "32px" }}
                    >
                        <Grid
                            item
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            Total External Budget
                        </Grid>
                        <Grid item>
                            {formattedCurrency(
                                group.external_budget,
                                projectCurrency,
                                false,
                                2,
                            )}
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        direction="column"
                        flexWrap="noWrap"
                        alignItems="flex-end"
                        sx={{ width: "auto", marginRight: "32px" }}
                    >
                        <Grid
                            item
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            Total Running
                        </Grid>
                        <Grid item>
                            {formattedCurrency(
                                group.running,
                                projectCurrency,
                                false,
                                2,
                            )}
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        direction="column"
                        flexWrap="noWrap"
                        alignItems="flex-end"
                        sx={{ width: "auto", marginRight: "16px" }}
                    >
                        <Grid
                            item
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            Total Actual
                        </Grid>
                        <Grid
                            item
                            sx={{
                                color:
                                    group.actual > group.external_budget
                                        ? theme.palette.error.main
                                        : theme.palette.text.primary,
                            }}
                        >
                            {formattedCurrency(
                                group.actual,
                                projectCurrency,
                                false,
                                2,
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </AccordionSummary>
            <AccordionDetails
                sx={{
                    overflow: "auto",
                    background:
                        (isRoot || isLeaf) && theme.palette.background.default,
                }}
            >
                {children.map((child, index) => (
                    <ActualsGroup
                        key={index}
                        group={child}
                        registerSetExpand={registerSetExpand}
                        selectable={selectable}
                        getProject={getProject}
                    />
                ))}
                {hasLines && (
                    <>
                        <ActualHeaderLine
                            isCustom={group.isCustomLineGroup}
                            selectable={selectable}
                        />
                        <div>
                            {group.line_items.map(
                                (line, index) =>
                                    line.actual_line && (
                                        <ActualLine
                                            selectable={selectable}
                                            key={index}
                                            lineId={line.actual_line.id}
                                            isCustom={group.isCustomLineGroup}
                                            isMidLine={midLineIndex == index}
                                            getProject={getProject}
                                        />
                                    ),
                            )}
                        </div>
                    </>
                )}
                {group.isCustomLineGroup && (
                    <Button
                        sx={{ margin: 1 }}
                        onClick={() => {
                            dispatch(openLinePopup());
                        }}
                        variant="outlined"
                        startIcon={<AddCircleOutlineRoundedIcon />}
                    >
                        Add Actual Line
                    </Button>
                )}
            </AccordionDetails>
        </Accordion>
    );
}
